import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './EditCRMProjectModal.css'; // Reutiliza o mesmo CSS
import AppIcon from './icons/AppIcon';

export default function EditCRMMultiProjectModal({ isOpen, onClose, project, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    color: '',
    background_image: '',
    connection_ids: [], // Array de conexões para CRM Multi
    ai_agent_id: ''
  });

  const [connections, setConnections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAgentWarning, setShowAgentWarning] = useState(false);
  const [agentWarningMessage, setAgentWarningMessage] = useState('');
  const [agentConflict, setAgentConflict] = useState(false);

  useEffect(() => {
    if (isOpen && project) {
      loadData();
    }
  }, [isOpen, project]);

  const loadData = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));

      // Carregar dados do projeto
      // CRM Multi usa settings.crm_multi.connection_ids (array)
      const connectionIds = project.settings?.crm_multi?.connection_ids || [];
      setFormData({
        name: project.name || '',
        description: project.description || '',
        icon: project.icon || 'work',
        color: project.color || '#44e1ff',
        background_image: project.background_image || '',
        connection_ids: connectionIds,
        ai_agent_id: project.ai_agent_id || ''
      });

      // Carregar conexões
      const { data: connectionsData, error: connError } = await supabase
        .from('connections')
        .select('*')
        .eq('company_id', user.company_id)
        .eq('status', 'CONECTADO')
        .order('name');

      console.log('[EditCRMMulti] Conexões carregadas:', connectionsData);
      if (connError) console.error('Erro ao carregar conexões:', connError);
      setConnections(connectionsData || []);

      // Carregar agentes
      const { data: agentsData, error: agentsError } = await supabase
        .from('agentes')
        .select('*')
        .eq('empresa_id', user.company_id)
        .order('nome');

      console.log('[EditCRMMulti] Agentes carregados:', agentsData);
      if (agentsError) console.error('Erro ao carregar agentes:', agentsError);
      setAgents(agentsData || []);

      // Se já tem agente selecionado, carregar dados dele
      if (project.ai_agent_id) {
        const agent = agentsData?.find(a => a.id === project.ai_agent_id);
        setSelectedAgent(agent);
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle de conexão (múltiplas permitidas)
  const handleToggleConnection = (connectionId) => {
    setFormData(prev => {
      const currentIds = prev.connection_ids || [];
      const isSelected = currentIds.includes(connectionId);
      
      if (isSelected) {
        // Remover conexão
        return { ...prev, connection_ids: currentIds.filter(id => id !== connectionId) };
      } else {
        // Adicionar conexão
        return { ...prev, connection_ids: [...currentIds, connectionId] };
      }
    });
    
    // Se tem agente selecionado, verificar novamente
    if (selectedAgent) {
      handleAgentSelect(selectedAgent.id);
    }
  };

  const handleAgentSelect = async (agentId) => {
    if (!agentId) {
      setSelectedAgent(null);
      setFormData(prev => ({ ...prev, ai_agent_id: '' }));
      setShowAgentWarning(false);
      return;
    }

    const agent = agents.find(a => a.id === agentId);
    setSelectedAgent(agent);

    const currentConnectionIds = formData.connection_ids || [];

    console.log('[EditCRMMulti] Agente selecionado:', agent);
    console.log('[EditCRMMulti] Conexao ID do agente:', agent?.conexao_id);
    console.log('[EditCRMMulti] Connection IDs do projeto:', currentConnectionIds);

    if (agent?.conexao_id && !currentConnectionIds.includes(agent.conexao_id)) {
      // Agente vinculado a outra conexão que não está no projeto - CONFLITO
      setAgentConflict(true);
      setAgentWarningMessage(
        `⚠️ Este agente já está vinculado a uma conexão que não está neste projeto. Adicione a conexão "${agent.conexao_id}" ao projeto ou selecione outro agente.`
      );
      setShowAgentWarning(true);
      setFormData(prev => ({ ...prev, ai_agent_id: '' }));
    } else if (!agent?.conexao_id && currentConnectionIds.length > 0) {
      // Agente sem conexão - vai vincular à primeira conexão do projeto
      setAgentConflict(false);
      setAgentWarningMessage(
        `✓ O agente "${agent.nome}" será vinculado à primeira conexão deste projeto. Deseja continuar?`
      );
      setShowAgentWarning(true);
      setFormData(prev => ({ ...prev, ai_agent_id: agentId }));
    } else {
      // Agente já vinculado a uma conexão do projeto ou sem conexões no projeto
      setShowAgentWarning(false);
      setFormData(prev => ({ ...prev, ai_agent_id: agentId }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (agentConflict) {
      alert('Não é possível salvar com um agente que está vinculado a uma conexão não selecionada.');
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));

      // Atualizar projeto
      const { error: projectError } = await supabase
        .from('projects')
        .update({
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          color: formData.color,
          background_image: formData.background_image,
          ai_agent_id: formData.ai_agent_id || null,
          settings: {
            ...project.settings,
            crm_multi: {
              ...project.settings?.crm_multi,
              connection_ids: formData.connection_ids || []
            }
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);

      if (projectError) throw projectError;

      // Se selecionou um agente e ele não tem conexão, vincular à primeira conexão do projeto
      if (formData.ai_agent_id && formData.connection_ids.length > 0 && selectedAgent && !selectedAgent.conexao_id) {
        const { error: agentError } = await supabase
          .from('agentes')
          .update({
            conexao_id: formData.connection_ids[0]
          })
          .eq('id', formData.ai_agent_id);

        if (agentError) {
          console.error('Erro ao vincular agente à conexão:', agentError);
        }
      }

      alert('✅ Projeto atualizado com sucesso!');
      onSave();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      alert('Erro ao salvar projeto: ' + error.message);
    }
  };

  const icons = [
    'work', 'business_center', 'store', 'shopping_cart', 'local_mall',
    'trending_up', 'analytics', 'assessment', 'bar_chart', 'pie_chart',
    'groups', 'person', 'contacts', 'phone', 'email',
    'campaign', 'rocket_launch', 'star', 'favorite', 'flag'
  ];

  const colors = [
    '#44e1ff', '#b43bea', '#ff6b6b', '#4ecdc4', '#45b7d1',
    '#f7b731', '#5f27cd', '#00d2d3', '#ff9ff3', '#54a0ff',
    '#48dbfb', '#1dd1a1', '#feca57', '#ee5a6f', '#c44569'
  ];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="edit-crm-project-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Projeto CRM Multi Atendimento</h2>
          <button className="btn-close" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <AppIcon name="progress_activity" className="spinning" />
            <p>Carregando...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-content">
            {/* Informações Básicas */}
            <div className="form-section">
              <h3>Informações Básicas</h3>
              
              <div className="form-group">
                <label>Nome do Projeto *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  placeholder="Ex: CRM Multi Atendimento"
                />
              </div>

              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o objetivo deste projeto..."
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Ícone</label>
                  <div className="icon-selector">
                    {icons.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        className={`icon-option ${formData.icon === icon ? 'selected' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, icon }))}
                      >
                        <AppIcon name={icon} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Cor</label>
                  <div className="color-selector">
                    {colors.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`color-option ${formData.color === color ? 'selected' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData(prev => ({ ...prev, color }))}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>URL da Imagem de Fundo</label>
                <input
                  type="url"
                  value={formData.background_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, background_image: e.target.value }))}
                  placeholder="https://exemplo.com/imagem.jpg"
                />
                {formData.background_image && (
                  <div className="image-preview">
                    <img src={formData.background_image} alt="Preview" />
                  </div>
                )}
              </div>
            </div>

            {/* Configurações CRM Multi */}
            <div className="form-section">
              <h3>Configurações CRM Multi Atendimento</h3>

              <div className="form-group">
                <label>Conexões WhatsApp (Selecione múltiplas)</label>
                <div className="connections-list">
                  {connections.length === 0 ? (
                    <p className="no-connections">Nenhuma conexão disponível</p>
                  ) : (
                    connections.map(conn => (
                      <label key={conn.id} className="connection-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.connection_ids.includes(conn.id)}
                          onChange={() => handleToggleConnection(conn.id)}
                        />
                        <span className="connection-info">
                          <AppIcon name="chat" />
                          {conn.name} - {conn.number || conn.type}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <small>Selecione uma ou mais conexões para receber leads</small>
              </div>

              <div className="form-group">
                <label>Agente IA</label>
                <select
                  value={formData.ai_agent_id}
                  onChange={(e) => handleAgentSelect(e.target.value)}
                >
                  <option value="">Nenhum agente</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.nome}
                    </option>
                  ))}
                </select>
                <small>Agente para atendimento automático</small>
              </div>

              {/* Aviso sobre vinculação do agente */}
              {showAgentWarning && (
                <div className={`agent-warning ${agentConflict ? 'error' : 'info'}`}>
                  <AppIcon name={agentConflict ? 'error' : 'info'} />
                  <p>{agentWarningMessage}</p>
                </div>
              )}
            </div>

            {/* Botões */}
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button 
                type="submit" 
                className="btn-primary"
                disabled={agentConflict}
              >
                <AppIcon name="save" />
                Salvar Alterações
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
